import { filterNonValuesFromArray } from 'libs/array';
import {
  decodeArray,
  decodeDelimitedArray,
  decodeDelimitedNumericArray,
  decodeNumericArray,
  decodeString,
  encodeArray,
  encodeDelimitedArray,
  encodeDelimitedNumericArray,
  encodeNumericArray,
  encodeQueryParams,
  objectToSearchString,
  QueryParamConfig,
  QueryParamConfigMap,
} from 'use-query-params';

export const KampusBooleanParam: QueryParamConfig<boolean, boolean> = {
  encode: (value) => (value ? 'true' : 'false'),
  decode: (value) => decodeString(value) === 'true',
};

// An empty array encodes to undefined so the param is removed from the URL
// entirely (a lingering `?ids=` decodes identically to an absent param, so
// keeping it is pure noise).
function emptyAsUndefined<T, R>(encode: (value: T[]) => R) {
  return (value: T[] | null | undefined): R | undefined =>
    value == null || value.length === 0 ? undefined : encode(value);
}

export const KampusArrayParam: QueryParamConfig<string[], string[]> = {
  encode: emptyAsUndefined(encodeArray),
  decode: (val) => filterNonValuesFromArray(decodeArray(val)),
};

export const KampusDelimitedArrayParam: QueryParamConfig<string[], string[]> = {
  encode: emptyAsUndefined(encodeDelimitedArray),
  decode: (val) => filterNonValuesFromArray(decodeDelimitedArray(val)),
};

export const KampusCommaDelimitedArrayParam: QueryParamConfig<
  string[],
  string[]
> = {
  encode: emptyAsUndefined((val) => encodeDelimitedArray(val, ',')),
  decode: (val) => filterNonValuesFromArray(decodeDelimitedArray(val, ',')),
};

export const KampusNumericArrayParam: QueryParamConfig<number[], number[]> = {
  encode: emptyAsUndefined(encodeNumericArray),
  decode: (val) => filterNonValuesFromArray(decodeNumericArray(val)),
};

export const KampusDelimitedNumericArrayParam: QueryParamConfig<
  number[],
  number[]
> = {
  encode: emptyAsUndefined(encodeDelimitedNumericArray),
  decode: (val) => filterNonValuesFromArray(decodeDelimitedNumericArray(val)),
};

export const KampusCommaDelimitedNumericArrayParam: QueryParamConfig<
  number[],
  number[]
> = {
  encode: emptyAsUndefined((val) => encodeDelimitedNumericArray(val, ',')),
  decode: (val) =>
    filterNonValuesFromArray(decodeDelimitedNumericArray(val, ',')),
};

export const buildPathWithQueryParams = <Config extends QueryParamConfigMap>(
  path: string,
  configMap: Config,
  values: Partial<{ [K in keyof Config]: Parameters<Config[K]['encode']>[0] }>
): string => {
  const search = objectToSearchString(encodeQueryParams(configMap, values));

  return search ? `${path}?${search}` : path;
};
